import { Storage_Locality } from '@schema';
import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';

export const DEFAULT_POLICY_NAME = 'default';
export const REDUNDANCY_ENCODING_TYPE = 'raid6';
export const REDUNDANCY_REPLICA_TYPE = 'raid1';
export const DEFAULT_FAILURE_DOMAIN = 'host';
export const DEFAULT_REDUNDANCY_TYPE = REDUNDANCY_REPLICA_TYPE;

export const CUSTOM_REDUNDANCY_MAP = computedLocale(() => ({
  raid1: {
    key: 'custom',
    need: 1,
    redundancy: { type: REDUNDANCY_REPLICA_TYPE, m: 0 },
    label: i18n.t('Custom scheme'),
    recommended: false,
    reason: undefined,
    reason_id: undefined,
    overhead: 0
  },
  raid6: {
    key: 'custom',
    need: 1,
    redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 0, n: 0 },
    label: i18n.t('Custom scheme'),
    recommended: false,
    reason: undefined,
    reason_id: undefined,
    overhead: 0
  }
}));

export const NO_REDUNDANCY_MAP = computedLocale(() => ({
  raid1: {
    key: 'raid1_1',
    need: 1,
    redundancy: {
      type: REDUNDANCY_REPLICA_TYPE,
      m: 1
    },
    label: i18n.t('No redundancy'),
    overhead: 0,
    recommended: false,
    reason: undefined,
    reason_id: undefined
  },
  raid6: {
    key: 'raid6_1+0',
    need: 1,
    redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 1, n: 0 },
    label: i18n.t('No redundancy'),
    overhead: 0,
    recommended: false,
    reason: undefined,
    reason_id: undefined
  }
}));

export const REDUNDANCY_MAP = computedLocale(() => ({
  raid1: {
    raid1_1: {
      key: 'raid1_1',
      need: 1,
      redundancy: {
        type: REDUNDANCY_REPLICA_TYPE,
        m: 1
      },
      label: i18n.t('No redundancy'),
      overhead: 0
    },
    raid1_2: {
      key: 'raid1_2',
      need: 2,
      redundancy: {
        type: REDUNDANCY_REPLICA_TYPE,
        m: 2
      },
      label: i18n.t('{m} replicas', { m: 2 }),
      overhead: 100
    },
    raid1_3: {
      key: 'raid1_3',
      need: 3,
      redundancy: {
        type: REDUNDANCY_REPLICA_TYPE,
        m: 3
      },
      label: i18n.t('{m} replicas', { m: 3 }),
      overhead: 200
    },
  },

  raid6: {
    'raid6_1+0': {
      key: 'raid6_1+0',
      need: 1,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 1, n: 0 },
      label: i18n.t('No redundancy'),
      overhead: 0
    },
    'raid6_1+1': {
      key: 'raid6_1+1',
      need: 2,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 1, n: 1 },
      label: i18n.t('Encoding {m}+{n}', { m: 1, n: 1 }),
      overhead: 100
    },
    'raid6_1+2': {
      key: 'raid6_1+2',
      need: 3,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 1, n: 2 },
      label: i18n.t('Encoding {m}+{n}', { m: 1, n: 2 }),
      overhead: 200
    },
    'raid6_3+1': {
      key: 'raid6_3+1',
      need: 4,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 3, n: 1 },
      label: i18n.t('Encoding {m}+{n}', { m: 3, n: 1 }),
      overhead: 33
    },
    'raid6_3+2': {
      key: 'raid6_3+2',
      need: 5,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 3, n: 2 },
      label: i18n.t('Encoding {m}+{n}', { m: 3, n: 2 }),
      overhead: 67
    },
    'raid6_5+2': {
      key: 'raid6_5+2',
      need: 7,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 5, n: 2 },
      label: i18n.t('Encoding {m}+{n}', { m: 5, n: 2 }),
      overhead: 40
    },
    'raid6_7+2': {
      key: 'raid6_7+2',
      need: 9,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 7, n: 2 },
      label: i18n.t('Encoding {m}+{n}', { m: 7, n: 2 }),
      overhead: 29
    },
    'raid6_17+3': {
      key: 'raid6_17+3',
      need: 20,
      redundancy: { type: REDUNDANCY_ENCODING_TYPE, m: 17, n: 3 },
      label: i18n.t('Encoding {m}+{n}', { m: 17, n: 3 }),
      overhead: 18
    }
  }
}));

export const DEFAULT_STORAGE = 'vstorage';

export const POLICY_TYPE = {
  LOCAL: 'local',
  EXTERNAL: 'external'
};

export const POLICY_STORAGE_TYPE_MAP = {
  vstorage: POLICY_TYPE.LOCAL
};

export const POLICY_TYPE_MAPPING = computedLocale(() => ({
  [POLICY_TYPE.LOCAL]: i18n.t('Local storage cluster'),
  [POLICY_TYPE.EXTERNAL]: i18n.t('External storage'),
}));

export const STORAGE_LOCALITY = Storage_Locality;

export const THIN_PROVISIONING_RATIO = 0;

export const THICK_PROVISIONING_RATIO = 1;

export const STORAGE_LOCALITY_LABEL = computedLocale(() => ({
  [STORAGE_LOCALITY.SHARED]: i18n.t('Shared storage tiers'),
  [STORAGE_LOCALITY.LOCAL]: i18n.t('Local storage tiers'),
}));

export const STORAGE_LOCALITY_VSTORAGE_LABEL = computedLocale(() => ({
  [STORAGE_LOCALITY.SHARED]: i18n.t('Shared V/Storage tiers'),
  [STORAGE_LOCALITY.LOCAL]: i18n.t('Local V/Storage tiers'),
}));

export const REDUNDANCY_ALERT_MAP = computedLocale(() => ({
  insufficient_failure_domains: {
    type: 'info',
    dataTestid: 'info-warning',
    message: i18n.t('Not enough failure domains to provide high availability in case of single domain failure'),
  },
  no_redundancy: {
    type: 'warning',
    dataTestid: 'single-failure-warning',
    message: i18n.t('To benefit from high availability, select a mode other than "No redundancy"')
  },
  loss_redundancy: {
    type: 'warning',
    dataTestid: 'single-failure-warning',
    message: i18n.t('Stored data will not have redundancy in case of single domain failure')
  },
  high_reliability: {
    type: 'success',
    dataTestid: 'high_reliability',
    message: i18n.t('High reliability')
  },
  low_overhead: {
    type: 'success',
    dataTestid: 'low_overhead',
    message: i18n.t('Low overhead')
  },
  high_performance: {
    type: 'success',
    dataTestid: 'high_performace',
    message: i18n.t('High performance')
  }
}));
